"use client";
import { CategoryDto } from "@/__generated__/graphql";
import Arrow2DownIcon from "@/app/_components/Icons/Arrow2DownIcon";
import Arrow2LeftIcon from "@/app/_components/Icons/Arrow2LeftIcon";
import { KeyboardArrowDown, KeyboardArrowLeft } from "@mui/icons-material";
import { alpha, Box, Card, CardActionArea, CardContent, Collapse, Icon, Link, Stack, styled, Typography } from "@mui/material";
import { SimpleTreeView, TreeItem, treeItemClasses, useTreeViewApiRef } from "@mui/x-tree-view";
import NextLink from "next/link";
import { useEffect, useLayoutEffect, useState } from "react";
import { useEffectOnce, useMount } from "react-use";
import { CategoryDtoWithHref } from "./types/CategoryDtoWithHref";
import { usePathname } from "next/navigation";
import TickIcon from "@/app/_components/Icons/TickIcon";
const CustomTreeItem = styled(TreeItem)(({ theme }) => ({
  color: theme.palette.mode === "light" ? theme.palette.grey[800] : theme.palette.grey[200],
  [`& .${treeItemClasses.selected}`]: {
    backgroundColor: theme.palette.secondary["50"],
  },
  [`& .${treeItemClasses.content}`]: {
    borderRadius: theme.spacing(0.5),
    padding: theme.spacing(0.5, 1),
    margin: theme.spacing(0.2, 0),
  },
  [`& .${treeItemClasses.iconContainer}`]: {
    borderRadius: "50%",

    color: theme.palette.mode === "dark" && theme.palette.primary.contrastText,
    padding: theme.spacing(0, 1.2),
  },
  [`& .${treeItemClasses.groupTransition}`]: {
    marginLeft: 18,
    paddingLeft: 18,
    borderLeft: `1px dashed ${alpha(theme.palette.text.primary, 0.4)}`,
  },
}));

const TreeItemX = ({ category }: { category: CategoryDtoWithHref }) => {
  const pathname = usePathname();
  const isActive = pathname === category.href;
  return (
    <CustomTreeItem
      itemId={String(category.id)}
      aria-selected={isActive}
      label={
        <Link
          scroll={false}
          component={NextLink}
          href={category.href}
          underline="hover"
          onClick={(e) => {
            e.stopPropagation();
          }}
          width={"100%"}
        >
          <Stack width={"100%"} spacing={0.2} direction={"row"} alignItems={"center"} justifyContent={"space-between"}>
            <Typography variant={"body1"} component={"h2"} {...(isActive && { fontWeight: 700 })}>
              {category.title}
            </Typography>
            {isActive && (
              <Icon color="secondary">
                <TickIcon />
              </Icon>
            )}
          </Stack>
        </Link>
      }
    >
      {category.children?.map((child, childIndex) => {
        return <TreeItemX category={child as CategoryDtoWithHref} key={childIndex} />;
      })}
    </CustomTreeItem>
  );
};

type Props = {
  intiOpen?: boolean;
  targetCategory?: CategoryDtoWithHref | null;
  categories: CategoryDtoWithHref[];
};
export default function CategoriesSideFilter(props: Props) {
  const [defaultExpandedItems] = useState<string[]>(() => {
    const list: string[] = [];
    if (props.targetCategory) {
      list.push(String(props.targetCategory.id));
      const parent = props.categories.find((a) => a.children?.some((x) => x.id === props.targetCategory?.id));
      if (parent) {
        list.push(String(parent.id));
      }
    }
    return list;
  });
  const apiRef = useTreeViewApiRef();
  const [open, setOpen] = useState(props.intiOpen ?? true);
  useEffect(() => {
    const attemptScroll = () => {
      if (apiRef.current && defaultExpandedItems.length > 0) {
        // apiRef.current.focusItem({} as any,defaultExpandedItems[0])
        const element = apiRef.current.getItemDOMElement(defaultExpandedItems[0]);

        if (element) {
          element.scrollIntoView({ block: "nearest", behavior: "smooth" });
        } else {
          console.warn("Element not found in tree view");
        }
      } else {
        // Retry after a short delay if not available
        setTimeout(attemptScroll, 500);
      }
    };

    setTimeout(attemptScroll, 500);
  }, [apiRef.current, defaultExpandedItems]);

  return (
    <Box
      sx={{
        borderRadius: 2,
      }}
    >
      <Card
        elevation={0}
        sx={{
          borderRadius: 0,
        }}
      >
        <CardActionArea
          onClick={(ev) => {
            setOpen((e) => !e);
            // handleScrollToChartsCommunity(ev);
          }}
          sx={{
            p: {
              md: 2,
              xs: 1,
            },
          }}
        >
          <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"}>
            <Typography variant="subtitle1">دسته بندی</Typography>
            <Icon color="secondary" fontSize="small">
              {!open ? <Arrow2LeftIcon /> : <Arrow2DownIcon />}
            </Icon>
          </Stack>
        </CardActionArea>
        <Collapse in={open}>
          <CardContent sx={{ maxHeight: 350, overflow: "auto" }} className="custom-scrollbar">
            <SimpleTreeView
              defaultExpandedItems={defaultExpandedItems}
              apiRef={apiRef}
              slots={{
                expandIcon: KeyboardArrowLeft,
                collapseIcon: KeyboardArrowDown,
              }}
            >
              {props &&
                props.categories &&
                props.categories.map((category, index) => {
                  return <TreeItemX category={category as CategoryDtoWithHref} key={index} />;
                })}
            </SimpleTreeView>
          </CardContent>
        </Collapse>
      </Card>
    </Box>
  );
}
